/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { isPresent } from '@ember/utils';
import { inject as service } from '@ember/service';

import DeliveryModel from 'cio/models/delivery';
import AuthService from 'cio/services/auth';
import { task } from 'ember-concurrency';
import DeliveryDetailModel from 'cio/models/delivery-detail';
import AlertsService from 'cio/services/alerts';

const DEFAULT_DETAIL_ROUTE = 'outbox.deliveries.delivery';

interface RowArgs {
  delivery: DeliveryModel;
  detailRoute: string;
  selected: boolean;
  onDelete: () => unknown;
  onRedeliver: (options: { tracked: boolean; overrideLimit?: boolean }) => unknown;
  onRetry: () => unknown;
  onSend: () => unknown;
  onToggleSelected: () => void;
}

export default class Row extends Component<RowArgs> {
  @service auth!: AuthService;
  @service alerts!: AlertsService;

  @tracked isExpanded = false;
  @tracked detail!: DeliveryDetailModel | null;
  @tracked hasReadOnlyPermission = false;

  get detailRoute() {
    return this.args.detailRoute ?? DEFAULT_DETAIL_ROUTE;
  }
  get recipient() {
    let { actionType, pushRecipient, recipient } = this.args.delivery;
    return actionType === 'push_action' ? pushRecipient : recipient;
  }

  @computed('args.delivery.{hasBeenSent,pendingDelete,pendingRetry,pendingSend}')
  get pendingStatus() {
    let { hasBeenSent, pendingDelete, pendingRetry, pendingSend } = this.args.delivery;
    if (hasBeenSent) {
      return;
    }

    if (pendingDelete) {
      return {
        label: 'Deleting soon!',
        class: 'text-error',
      };
    } else if (pendingRetry) {
      return {
        label: 'Retrying soon!',
        class: 'text-success',
      };
    } else if (pendingSend) {
      return {
        label: 'Sending soon!',
        class: 'text-success',
      };
    } else {
      return;
    }
  }

  fetchDeliveryRelationships = task({ drop: true }, async () => {
    if (this.args.delivery?.newsletterId != null) {
      this.hasReadOnlyPermission = !this.auth.hasPermission('journeys/newsletters/edit');
    } else if (this.args.delivery?.transactionalMessageId != null) {
      this.hasReadOnlyPermission = !this.auth.hasPermission('journeys/transactional_messages/edit');
    } else if (this.args.delivery?.campaignId != null) {
      let campaign = await this.args.delivery.belongsTo('campaign').load();

      if (campaign.isApiTriggered === true) {
        this.hasReadOnlyPermission = !this.auth.hasPermission('journeys/broadcasts/edit');
      } else {
        this.hasReadOnlyPermission = !this.auth.hasPermission('journeys/campaigns/edit');
      }
    }
  });

  @action
  redeliver({ tracked, overrideLimit }: { tracked: boolean; overrideLimit?: boolean }) {
    let options: { tracked: boolean; overrideLimit?: boolean } = { tracked };

    if (isPresent(overrideLimit)) {
      options.overrideLimit = overrideLimit;
    }

    return this.args.onRedeliver(options);
  }

  @action
  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  fetchDetailTask = task({ drop: true }, async () => {
    try {
      this.detail = await this.args.delivery.belongsTo('detail').load();
    } catch {
      this.alerts.error('delivery.detail-error', "We weren't able to load the delivery detail");
    }
  });

  constructor(owner: unknown, args: RowArgs) {
    super(owner, args);
    this.fetchDetailTask.perform();
    this.fetchDeliveryRelationships.perform();
  }
}
